/**
 * tableSelection.ts
 *
 * Tier 1 view filtering for the dashboard chart widget (#968 Part A): which
 * rows and columns of the source table a chart shows. The table is never
 * altered — this is only the window into it.
 *
 * The column half is a RULE, not a result (decision D2, 2026-08-13). "Latest
 * three columns" means the most recent three at the moment the chart renders,
 * so a new wave moves it. Everything downstream — the graph engine, PPTX
 * export — takes a resolved list of names and never learns about the rule.
 * That resolution happens here.
 */

import {
  ColumnSelection,
  TableGraphSelection,
  TableGraphWidgetData,
  TableRange,
} from '../types/gotypes'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import { applyRangeToPlatinumData } from './tableRange'
import {
  extractRowLabels,
  extractColumnLabels,
  extractRowTypes,
  extractColumnTypes,
} from '../components/platinum-graphs/utils/convertPlatinumData'

/**
 * A selection resolved against real data — concrete names, no rules left.
 *
 * `rowIndices`/`columnIndices` are set when the selection is POSITIONAL
 * (#968 Obs 11): they are the authoritative slots, and the name arrays are
 * only the labels currently occupying them. When absent the selection is a
 * legacy name-based one and the name arrays are authoritative — which is
 * what keeps widgets saved before the positional model rendering unchanged.
 */
export interface ResolvedSelection {
  rows: string[]
  columns: string[]
  rowIndices?: number[]
  columnIndices?: number[]
  switchAxis: boolean
}

/**
 * Normalizes a positional pick against the axis it addresses: de-duplicates,
 * sorts into table order, and drops positions the table no longer has
 * (Obs 11: a slot that is no longer occupied simply isn't rendered).
 * Returns [] — "all" — when nothing valid remains or the pick covers every
 * position, so a stale or degenerate selection never blanks the chart.
 */
function normalizePositions(positions: number[] | undefined, axisLength: number): number[] {
  if (!positions || positions.length === 0) return []
  const valid = Array.from(new Set(positions))
    .filter(p => Number.isInteger(p) && p >= 0 && p < axisLength)
    .sort((a, b) => a - b)
  if (valid.length === 0 || valid.length === axisLength) return []
  return valid
}

/**
 * Resolves a column rule against the columns the table actually has.
 *
 * - `fixed` keeps its named columns, in table order, dropping any that have
 *   since disappeared from the table.
 * - `rolling` takes `count` columns from one end. `anchor: 'end'` (the
 *   default, and the case D2 was decided for) means the most recent.
 *
 * Returns every column when there is no rule, when the rule selects nothing
 * usable, or when a fixed selection has gone completely stale — a chart that
 * silently renders empty is worse than one that renders too much.
 */
export function resolveColumnSelection(
  columns: ColumnSelection | undefined,
  allColumns: string[],
): string[] {
  if (!columns) return allColumns
  if (allColumns.length === 0) return []

  if (columns.mode === 'rolling') {
    const count = columns.count ?? 0
    if (count <= 0) return allColumns
    if (count >= allColumns.length) return allColumns
    return columns.anchor === 'start'
      ? allColumns.slice(0, count)
      : allColumns.slice(allColumns.length - count)
  }

  // fixed
  const wanted = new Set(columns.names || [])
  if (wanted.size === 0) return allColumns
  const kept = allColumns.filter(name => wanted.has(name))
  return kept.length > 0 ? kept : allColumns
}

/**
 * Converts a legacy contiguous `range` into an equivalent selection.
 *
 * Widgets saved before the selection model carry `range`, and their charts
 * must keep rendering the same slice. Rather than re-derive the row/column
 * mapping (a side vec can span several data rows, so indices are not labels),
 * this applies the range with the existing, tested code and reads the labels
 * back off the result.
 *
 * A range was always a fixed pick of adjacent columns, never a rolling one, so
 * it converts to `mode: 'fixed'`.
 */
export function selectionFromLegacyRange(
  range: TableRange,
  data: PlatinumData,
): TableGraphSelection {
  const sliced = applyRangeToPlatinumData(data, range)
  return {
    rows: extractRowLabels(sliced),
    columns: { mode: 'fixed', names: extractColumnLabels(sliced) },
  }
}

/**
 * The selection a widget should render by, whichever era it was saved in.
 *
 * `selection` wins. A widget that only has `range` is converted on the fly, so
 * it renders identically until someone edits and re-saves it. Returns
 * undefined when the widget filters nothing.
 */
export function effectiveSelection(
  widget: Pick<TableGraphWidgetData, 'selection' | 'range'>,
  data: PlatinumData | undefined,
): TableGraphSelection | undefined {
  if (widget.selection) return widget.selection
  if (widget.range && data) return selectionFromLegacyRange(widget.range, data)
  return undefined
}

/**
 * Resolves a selection against loaded data, ready to hand to the graph engine
 * as `selected_rows` / `selected_columns` / `switch_axis`.
 *
 * Empty arrays mean "everything", which is what those fields already mean to
 * the engine — so an absent selection resolves to empty, not to a full list.
 */
export function resolveSelection(
  selection: TableGraphSelection | undefined,
  data: PlatinumData | undefined,
): ResolvedSelection {
  if (!selection || !data) {
    return { rows: [], columns: [], switchAxis: selection?.switch_axis || false }
  }

  const allRows = extractRowLabels(data)
  const allColumns = extractColumnLabels(data)

  // Positional rows (#968 Obs 11) win over the legacy name list.
  let rows: string[]
  let rowIndices: number[] | undefined
  const positionalRows = normalizePositions(selection.row_positions, allRows.length)
  if ((selection.row_positions?.length ?? 0) > 0) {
    rowIndices = positionalRows
    rows = positionalRows.map(p => allRows[p])
  } else {
    const wantedRows = new Set(selection.rows || [])
    rows = wantedRows.size > 0
      ? allRows.filter(name => wantedRows.has(name))
      : []
  }

  // Positional columns win over both the pin and the rule: a positional pick
  // cannot drift with new waves, so it needs no pin. Otherwise a pin from
  // sign-off outranks the rule: the signed view must not move when a new wave
  // lands. Columns that have since vanished drop out; if the whole pin has
  // gone stale the rule takes over again rather than emptying the chart.
  let columns: string[]
  let columnIndices: number[] | undefined
  const positionalColumns = normalizePositions(selection.column_positions, allColumns.length)
  if ((selection.column_positions?.length ?? 0) > 0) {
    columnIndices = positionalColumns
    columns = positionalColumns.map(p => allColumns[p])
  } else {
    const pinned = (selection.pinned_columns || []).filter(name => allColumns.includes(name))
    const resolvedColumns = pinned.length > 0
      ? pinned
      : resolveColumnSelection(selection.columns, allColumns)
    // A rule that resolves to every column is not a filter — send empty so the
    // engine treats it as unfiltered rather than pinning today's column list.
    columns = resolvedColumns.length === allColumns.length ? [] : resolvedColumns
  }

  return {
    rows,
    columns,
    rowIndices,
    columnIndices,
    switchAxis: selection.switch_axis || false,
  }
}

/** The row positions a selection currently keeps; empty means all. */
function effectiveRowIndices(
  selection: TableGraphSelection | undefined,
  allRows: string[],
): number[] {
  if (!selection) return []
  if ((selection.row_positions?.length ?? 0) > 0) {
    return normalizePositions(selection.row_positions, allRows.length)
  }
  const wanted = new Set(selection.rows || [])
  if (wanted.size === 0) return []
  const kept = allRows
    .map((name, index) => ({ name, index }))
    .filter(({ name }) => wanted.has(name))
    .map(({ index }) => index)
  return kept.length === allRows.length ? [] : kept
}

/** The column positions a selection currently keeps; empty means all. */
function effectiveColumnIndices(
  selection: TableGraphSelection | undefined,
  allColumns: string[],
): number[] {
  if (!selection) return []
  if ((selection.column_positions?.length ?? 0) > 0) {
    return normalizePositions(selection.column_positions, allColumns.length)
  }
  const pinned = (selection.pinned_columns || []).filter(name => allColumns.includes(name))
  const names = pinned.length > 0 ? pinned : resolveColumnSelection(selection.columns, allColumns)
  const wanted = new Set(names)
  const kept = allColumns
    .map((name, index) => ({ name, index }))
    .filter(({ name }) => wanted.has(name))
    .map(({ index }) => index)
  return kept.length === allColumns.length ? [] : kept
}

/**
 * The positions each axis of a selection currently keeps, whichever era the
 * selection was saved in — positional directly, legacy name/rule forms mapped
 * onto the positions they occupy today. Empty means "all" on that axis.
 * This is the editor's display seam (#968 Obs 10): the Select Data dropdowns
 * check exactly these slots.
 */
export function effectiveSelectionPositions(
  selection: TableGraphSelection | undefined,
  data: PlatinumData,
): { rowPositions: number[]; columnPositions: number[] } {
  return {
    rowPositions: effectiveRowIndices(selection, extractRowLabels(data)),
    columnPositions: effectiveColumnIndices(selection, extractColumnLabels(data)),
  }
}

/**
 * Builds a clean positional selection from explicit position picks (#968
 * Obs 10/11). Positions covering the whole axis are stored as "all" (absent),
 * so an unfiltered widget carries no filter — and "all" then refreshes with
 * whatever the table holds next. Returns undefined when nothing is filtered
 * and the axes aren't switched. Legacy name/rule/pin fields are deliberately
 * NOT carried over: any edit through the new editor converts the widget to
 * the positional model.
 */
export function buildPositionalSelection(
  rowPositions: number[],
  columnPositions: number[],
  switchAxis: boolean,
  data: PlatinumData,
): TableGraphSelection | undefined {
  const rows = normalizePositions(rowPositions, extractRowLabels(data).length)
  const columns = normalizePositions(columnPositions, extractColumnLabels(data).length)
  const next: TableGraphSelection = {}
  if (rows.length > 0) next.row_positions = rows
  if (columns.length > 0) next.column_positions = columns
  if (switchAxis) next.switch_axis = true
  return Object.keys(next).length > 0 ? next : undefined
}

const isBaseType = (type: string | undefined): boolean => (type || '').toLowerCase() === 'base'

/**
 * Base vecs repeat the same label per brand group ("Cases WF" × 10), which is
 * indistinguishable in a flat list. Suffix each base vec with the label of the
 * next data vec — its group-mate — so the entry reads "Cases WF (Sky)".
 *
 * Lives here rather than in an editor because the CHART editor no longer needs
 * it (#968 comment 3 excludes bases from its dropdowns entirely) while the
 * TABLE editor does: bases are selectable in table mode (#969), so its Rows
 * dropdown still has to tell ten identical "Cases WF" entries apart.
 */
export function disambiguateBaseLabels(labels: string[], types: string[]): string[] {
  return labels.map((label, index) => {
    if (!isBaseType(types[index])) return label
    for (let next = index + 1; next < labels.length; next++) {
      if (!isBaseType(types[next])) return `${label} (${labels[next]})`
    }
    return label
  })
}

/**
 * The vecs a CHART can actually draw (#968 comment 3). Charts never render
 * base rows, so offering them in the Rows/Columns dropdowns gives dead
 * options — deselecting "Cases WF (Sky)" changes nothing on screen.
 *
 * Returns the labels to offer AND the true vec positions they came from,
 * because POSITION is the selection contract: once bases are filtered out the
 * dropdown's option index is no longer the vec index, and filtering the label
 * array alone would silently re-point every selection.
 *
 * Falls back to every vec when an axis is only bases — a dropdown with no
 * options is a worse failure than one with useless options (same stance as
 * defaultDataOnlySelection).
 */
export function chartableAxis(
  labels: string[],
  types: string[],
): { options: string[]; positions: number[] } {
  const dataPositions = labels.map((_, index) => index).filter(index => !isBaseType(types[index]))
  const positions = dataPositions.length > 0 ? dataPositions : labels.map((_, index) => index)
  return { options: positions.map(index => labels[index]), positions }
}

/**
 * True vec positions → dropdown option indices, for the axis `chartableAxis`
 * produced. Positions with no option (bases) drop out of the display; an empty
 * pick stays empty, which the dropdown reads as "all".
 */
export function toDisplayPositions(selected: number[], axisPositions: number[]): number[] {
  if (selected.length === 0) return []
  const optionIndex = new Map(axisPositions.map((position, index) => [position, index]))
  return selected
    .map(position => optionIndex.get(position))
    .filter((index): index is number => index !== undefined)
    .sort((a, b) => a - b)
}

/**
 * Dropdown option indices → true vec positions, the inverse of
 * `toDisplayPositions`. An empty pick means "all", so it expands to every
 * chartable position.
 *
 * `preserved` carries the positions the dropdown never showed — base positions
 * an existing widget already stored. They survive an edit to some other row
 * rather than being silently dropped.
 */
export function fromDisplayPositions(
  display: number[],
  axisPositions: number[],
  preserved: number[] = [],
): number[] {
  const picked = display.length === 0
    ? axisPositions
    : display.map(index => axisPositions[index]).filter(position => position !== undefined)
  return Array.from(new Set([...preserved, ...picked])).sort((a, b) => a - b)
}

/**
 * The default selection for a freshly configured chart (#968 Obs 10.4):
 * only actual data rows and columns — vecs whose type is 'Base' (totals,
 * bases, the "filled" rows) start deselected. Returns undefined when there is
 * nothing to exclude (the default is then simply "all").
 */
export function defaultDataOnlySelection(data: PlatinumData): TableGraphSelection | undefined {
  const rowTypes = extractRowTypes(data)
  const columnTypes = extractColumnTypes(data)
  const dataPositions = (types: string[]): number[] => {
    const kept = types
      .map((type, index) => ({ type, index }))
      .filter(({ type }) => type.toLowerCase() !== 'base')
      .map(({ index }) => index)
    // Nothing excluded, or nothing left: no filter on that axis.
    return kept.length === types.length || kept.length === 0 ? [] : kept
  }
  return buildPositionalSelection(dataPositions(rowTypes), dataPositions(columnTypes), false, data)
}

/**
 * Drops one series from the selection, for a legend click on the chart (#968
 * Part A — filtering that reads off the visual rather than a separate tool).
 *
 * Which axis a legend series belongs to depends on the transpose: normally the
 * series are rows (brands), and with switch_axis they are columns (waves).
 *
 * Emits a POSITIONAL selection (#968 Obs 11): whatever form the selection was
 * in, the result keeps the surviving slots by position. Every slot whose label
 * matches the clicked series is dropped — the legend cannot tell duplicates
 * apart, which matches what the click means visually.
 *
 * Refuses to remove the last series — an empty chart reads as broken, and the
 * viewer would have no legend left to undo it with.
 */
export function removeSeriesFromSelection(
  selection: TableGraphSelection | undefined,
  seriesName: string,
  data: PlatinumData,
): TableGraphSelection | undefined {
  const allRows = extractRowLabels(data)
  const allColumns = extractColumnLabels(data)

  if (selection?.switch_axis) {
    const current = effectiveColumnIndices(selection, allColumns)
    const effective = current.length > 0 ? current : allColumns.map((_, i) => i)
    const kept = effective.filter(index => allColumns[index] !== seriesName)
    if (kept.length === 0 || kept.length === effective.length) return selection
    const { columns: _rule, pinned_columns: _pin, ...rest } = selection
    return { ...rest, column_positions: kept }
  }

  const current = effectiveRowIndices(selection, allRows)
  const effective = current.length > 0 ? current : allRows.map((_, i) => i)
  const kept = effective.filter(index => allRows[index] !== seriesName)
  if (kept.length === 0 || kept.length === effective.length) return selection
  const { rows: _legacyRows, ...rest } = selection || {}
  return { ...rest, row_positions: kept }
}

/**
 * Applies a selection to the data, returning only the selected rows and
 * columns.
 *
 * The widget renders in both table and graph mode from one PlatinumData, and
 * the graph engine's `selected_rows`/`selected_columns` only reach the graph
 * half. Filtering the data itself covers both, and is what the superseded
 * `range` did — so a converted legacy widget keeps rendering exactly as before.
 *
 * Empty rows or columns mean "keep everything on that axis". Groups are
 * cleared, as they are for ranges: a sliced axis invalidates them.
 */
/**
 * The minimal shape `resolveRowColumnFilters` needs — a subset of
 * `ResolvedSelection` so `tableViewConfig.applySelectionToTableData` (which
 * has no `switchAxis`) can share it too (#1066 item 3).
 */
export interface RowColumnSelectionInput {
  rows: string[]
  columns: string[]
  rowIndices?: number[]
  columnIndices?: number[]
}

export interface RowColumnFilters {
  filterRows: boolean
  filterColumns: boolean
  keepRow: (label: string, index: number) => boolean
  keepColumn: (label: string, index: number) => boolean
}

/**
 * Turns a resolved row/column selection into "should this row/column stay"
 * predicates. Positional selections filter by INDEX (#968 Obs 11), which is
 * what makes two rows sharing a label independently selectable — the name
 * path stays for widgets saved before the positional model.
 *
 * Previously duplicated character-for-character between
 * `applySelectionToPlatinumData` (here) and `tableViewConfig.applySelectionToTableData`.
 */
export function resolveRowColumnFilters(resolved: RowColumnSelectionInput): RowColumnFilters {
  const wantedRowIndices = new Set(resolved.rowIndices || [])
  const wantedColumnIndices = new Set(resolved.columnIndices || [])
  const wantedRows = new Set(resolved.rows)
  const wantedColumns = new Set(resolved.columns)
  const filterRows = resolved.rowIndices ? wantedRowIndices.size > 0 : wantedRows.size > 0
  const filterColumns = resolved.columnIndices ? wantedColumnIndices.size > 0 : wantedColumns.size > 0

  return {
    filterRows,
    filterColumns,
    keepRow: (label: string, index: number): boolean =>
      resolved.rowIndices ? wantedRowIndices.has(index) : wantedRows.has(label),
    keepColumn: (label: string, index: number): boolean =>
      resolved.columnIndices ? wantedColumnIndices.has(index) : wantedColumns.has(label),
  }
}

/**
 * Pins a name-based row selection to the positions those names hold in
 * `data` (#1808). A chart that relabels its rows (category_label) filters the
 * relabelled table, where the saved names no longer exist; positions survive
 * the relabel. Positional selections, and selections with no rows, come back
 * unchanged.
 */
export function rowsAsPositions(resolved: ResolvedSelection, data: PlatinumData): ResolvedSelection {
  if (resolved.rowIndices || resolved.rows.length === 0) return resolved
  const wanted = new Set(resolved.rows)
  const rowIndices = (data.side?.vecs || [])
    .map((vec, index) => (wanted.has(vec.label || '') ? index : -1))
    .filter((index) => index >= 0)
  return { ...resolved, rowIndices }
}

export function applySelectionToPlatinumData(
  data: PlatinumData,
  resolved: ResolvedSelection,
): PlatinumData {
  const sideVecs = data.side?.vecs || []
  const topVecs = data.top?.vecs || []
  const cellRows = data.cells?.rows || []

  const { filterRows, filterColumns, keepRow, keepColumn } = resolveRowColumnFilters(resolved)
  if (!filterRows && !filterColumns) return data

  // `cells.rows` is indexed by SIDE VEC, not by band row: one row of cells per
  // vec, with the bands (freq/colpc/rowpc) as fields on each cell. See
  // GetPCells in carbon/RCS.Carbon.Tables/OutputFormatters/PlatinumFormatter.cs,
  // and platinumToGrid, which reads `rows[sideIdx]`.
  //
  // This used to walk a running band offset, which silently agreed with the vec
  // index only while every vec rendered as exactly one band — so switching
  // column percents on shifted every filtered chart onto the wrong rows.
  const keptCellRowIndices: number[] = []
  const keptSideVecs: typeof sideVecs = []
  sideVecs.forEach((vec, index) => {
    if (filterRows && !keepRow(vec.label || '', index)) return
    keptSideVecs.push(vec)
    keptCellRowIndices.push(index)
  })

  const keptColumnIndices = topVecs
    .map((vec, index) => ({ vec, index }))
    .filter(({ vec, index }) => !filterColumns || keepColumn(vec.label || '', index))

  // Never hand back an empty table — that reads as a broken widget rather than
  // a filtered one. resolveSelection already guards the common causes.
  if (keptSideVecs.length === 0 || keptColumnIndices.length === 0) return data

  const newCellRows = keptCellRowIndices
    .map(rowIndex => cellRows[rowIndex])
    .filter(Boolean)
    .map(row => ({
      ...row,
      cell: keptColumnIndices.map(({ index }) => (row.cell || [])[index]).filter(Boolean),
    }))

  return {
    ...data,
    top: {
      ...data.top,
      groups: [],
      vecs: keptColumnIndices.map(({ vec }) => vec),
    },
    side: {
      ...data.side,
      groups: [],
      vecs: keptSideVecs,
    },
    cells: {
      ...data.cells,
      rows: newCellRows,
    },
  }
}

/**
 * Chart types that plot exactly ONE column (#1172): the generators for these
 * take a single `selectedHeader`, so a multi-column selection is a lie — the
 * chart silently plots the first column of the slice while the Columns
 * dropdown claims more. The editor swaps the multi dropdown for a single
 * selector on these types, and switching TO one of them collapses the stored
 * column selection to its first chartable column.
 */
export const SINGLE_COLUMN_CHART_TYPES = ['bar', 'column', 'pie'] as const

export function isSingleColumnChartType(chartType: string | undefined): boolean {
  return (SINGLE_COLUMN_CHART_TYPES as readonly string[]).includes(chartType || '')
}
